import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DateTime } from "luxon";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Clubhouse_LgAdjudicationIssueTypeV2,
  Clubhouse_LgAdjudicationIssueSeverityV2,
} from "../../enums";
import type { Clubhouse_LeagueAdjudicationQueueIssueDto } from "../../types/adjudicationQueue";

type Issue = Clubhouse_LeagueAdjudicationQueueIssueDto;

export const issueTypeLabel: Record<
  Clubhouse_LgAdjudicationIssueTypeV2,
  string
> = {
  [Clubhouse_LgAdjudicationIssueTypeV2.UNKNOWN]: "Unknown Issue",
  [Clubhouse_LgAdjudicationIssueTypeV2.MISSING_SCORECARD]: "Missing Scorecard",
  [Clubhouse_LgAdjudicationIssueTypeV2.WRONG_COURSE]: "Wrong Course Detected",
  [Clubhouse_LgAdjudicationIssueTypeV2.DUPLICATE_SAVEGAME]:
    "Duplicate Savegames",
  [Clubhouse_LgAdjudicationIssueTypeV2.INVALID_SCORECARD]: "Invalid Scorecard",
  [Clubhouse_LgAdjudicationIssueTypeV2.ROUND_DATA_MISMATCH]:
    "Round Data Mismatch",
  [Clubhouse_LgAdjudicationIssueTypeV2.IMPORT_VALIDATION]: "Import Validation",
  [Clubhouse_LgAdjudicationIssueTypeV2.ORPHAN_RECOVERY]: "Orphan Recovery",
};

export const severityLabel: Record<
  Clubhouse_LgAdjudicationIssueSeverityV2,
  string
> = {
  [Clubhouse_LgAdjudicationIssueSeverityV2.Info]: "Info",
  [Clubhouse_LgAdjudicationIssueSeverityV2.Warning]: "Warning",
  [Clubhouse_LgAdjudicationIssueSeverityV2.Blocking]: "Blocking",
};

// Higher = more urgent; used to prioritize blocking issues first.
export const severityRank: Record<
  Clubhouse_LgAdjudicationIssueSeverityV2,
  number
> = {
  [Clubhouse_LgAdjudicationIssueSeverityV2.Info]: 0,
  [Clubhouse_LgAdjudicationIssueSeverityV2.Warning]: 1,
  [Clubhouse_LgAdjudicationIssueSeverityV2.Blocking]: 2,
};

// Sort rank for the Severity column; an unknown/missing severity sorts lowest.
const rankSeverity = (
  severity: Clubhouse_LgAdjudicationIssueSeverityV2 | undefined,
): number => (severity != null ? severityRank[severity] : -1);

// Blocking severity tints the row's leading icon red; warnings amber; info muted.
function severityIconClass(
  severity?: Clubhouse_LgAdjudicationIssueSeverityV2,
): string {
  if (severity === Clubhouse_LgAdjudicationIssueSeverityV2.Blocking)
    return "text-destructive";
  if (severity === Clubhouse_LgAdjudicationIssueSeverityV2.Warning)
    return "text-amber-500";
  return "text-text-muted";
}

function SeverityBadge({
  severity,
}: {
  severity?: Clubhouse_LgAdjudicationIssueSeverityV2;
}) {
  if (severity == null)
    return <span className="text-xs text-text-muted">—</span>;
  if (severity === Clubhouse_LgAdjudicationIssueSeverityV2.Blocking)
    return <Badge variant="destructive">Blocking</Badge>;
  if (severity === Clubhouse_LgAdjudicationIssueSeverityV2.Warning)
    return (
      <Badge
        variant="outline"
        className="border-transparent bg-amber-500/10 text-amber-600 dark:text-amber-400"
      >
        Warning
      </Badge>
    );
  return <Badge variant="outline">Info</Badge>;
}

function initials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

export function getAdjudicationColumns(
  onReview: (issue: Issue) => void,
): ColumnDef<Issue>[] {
  return [
    {
      accessorKey: "issueType",
      header: "Issue",
      enableSorting: false,
      cell: ({ row }) => {
        const type = row.original.issueType;
        const label = type != null ? issueTypeLabel[type] : "Unknown Issue";
        return (
          <div className="flex items-center gap-2.5">
            <AlertTriangle
              className={cn(
                "size-4 shrink-0",
                severityIconClass(row.original.severity),
              )}
            />
            <span className="text-xs font-semibold text-text-primary poppins">
              {label}
            </span>
          </div>
        );
      },
    },
    {
      id: "leagueRound",
      header: "League & Round",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex min-w-0 max-w-[220px] flex-col">
          <span className="truncate text-xs font-medium text-text-primary poppins">
            {row.original.leagueName ?? "—"}
          </span>
          {row.original.roundName && (
            <span className="truncate text-xs text-text-muted mulish">
              {row.original.roundName}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "participant",
      header: "Participant",
      enableSorting: false,
      cell: ({ row }) => {
        const name = row.original.participantName;
        if (!name) return <span className="text-xs text-text-muted">—</span>;
        return (
          <div className="flex items-center gap-2">
            <Avatar className="size-7">
              <AvatarFallback className="text-[10px] font-medium">
                {initials(name)}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium text-text-primary poppins">
              {name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "severity",
      header: "Severity",
      cell: ({ row }) => <SeverityBadge severity={row.original.severity} />,
      sortingFn: (a, b) =>
        rankSeverity(a.original.severity) - rankSeverity(b.original.severity),
    },
    {
      accessorKey: "createdUtc",
      header: "Created",
      cell: ({ row }) => {
        const iso = row.original.createdUtc;
        const rel = iso ? DateTime.fromISO(iso).toRelative() : null;
        return (
          <span className="text-xs text-text-muted mulish whitespace-nowrap">
            {rel ?? "—"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      // Label-less: keep it out of the "Toggle columns" menu.
      enableHiding: false,
      enableSorting: false,
      meta: { className: "text-right" },
      cell: ({ row }) => (
        <Button
          type="button"
          size="sm"
          className="bg-primary font-semibold text-white text-xs h-7 px-3 rounded-md"
          // Review navigates to the associated league/round; with no league to
          // target there's nowhere to go, so don't offer a dead button.
          disabled={row.original.leagueId == null}
          onClick={() => onReview(row.original)}
        >
          Review
        </Button>
      ),
    },
  ];
}
